// Raw MySQL campaign rows shared by catalog cards and the public campaign search renderer.
import { decodeHtmlEntitiesForPlainText as plain } from './decode-html-entities';
import { ftClockCalendarFromRaw, ftClockColsFromArr, ftFormatCampaignTime } from './campaign-clock';
import { compilePictureRow } from '../janus-engine/compile/picture-row-construction';
import type { GameCard, MyCampaignCard } from '../catalog-list/types';

export interface CampaignListRow extends Record<string, unknown> {
    campaign_id: string;
    campaign_name: string | null;
}
export interface CampaignListResponse {
    schema: 'campaign-list-v1';
    mode: 'dashboard' | 'games' | 'search';
    campaigns: CampaignListRow[];
    viewer_id?: string;
    viewer_staff?: boolean;
    viewer_timezone?: string;
    csrf_token?: string;
    total: number;
}

/** PDO returns JSON aggregates as strings. Keep parsing and all display decisions in the browser. */
export function rawListArray(value: unknown): unknown[] {
    if (value == null || value === '') return [];
    const rows: unknown = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(rows)) throw new Error('campaign-list:invalid-aggregate');
    return rows;
}
export const rawListRecord = (value: unknown): Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
export function requireCampaignList(value: unknown): CampaignListResponse {
    const data = value as CampaignListResponse;
    if (data?.schema !== 'campaign-list-v1' || !Array.isArray(data.campaigns)) throw new Error('campaign-list-schema');
    return data;
}

/** A schema-bearing error response still supplies usable (often empty) rows. */
export function parseCampaignRowsResponse(raw: string, status: number, schema: 'campaign-list-v1' | 'campaign-roster-v1'): unknown {
    let payload: unknown;
    try { payload = JSON.parse(raw); }
    catch { throw new Error(`Non-JSON response (HTTP ${status}): ${raw.slice(0, 300)}`); }
    const record = rawListRecord(payload);
    if (record.schema !== schema && (status >= 400 || record.error)) {
        const message = [record.detail, record.message, record.error].find(value => typeof value === 'string' && value !== '');
        throw new Error(typeof message === 'string' ? message : `HTTP ${status}`);
    }
    return payload;
}

export function campaignCalendar(row: CampaignListRow) {
    const months = rawListArray(row.calendar_months).map(rawListRecord).sort((a, b) => Number(a.month_order) - Number(b.month_order));
    const weekdays = rawListArray(row.calendar_days).map(rawListRecord).sort((a, b) => Number(a.day_of_week) - Number(b.day_of_week));
    return ftClockCalendarFromRaw({ months, weekdays, max_month_order: Math.max(0, ...months.map(m => Number(m.month_order))),
        max_day_of_week: Math.max(0, ...weekdays.map(d => Number(d.day_of_week))), sum_days_in_year: months.reduce((sum, m) => sum + Number(m.days_in_month), 0) });
}
export function campaignWorldDate(row: CampaignListRow): string {
    return ftFormatCampaignTime(ftClockColsFromArr(row), campaignCalendar(row)).calendar_current_full_date_f;
}
export function campaignLevel(row: CampaignListRow): number {
    const levels = rawListArray(row.class_levels).map(v => rawListArray(v).reduce<number>((sum, level) => sum + Math.trunc(Number(level) || 0), 0));
    const average = levels.length ? Math.floor(levels.reduce((sum, level) => sum + level, 0) / levels.length) : 0;
    return average || Number(row.campaign_default_starting_level) || 0;
}

export function viewerTimezone(input?: string, fallback = 'UTC'): string {
    try { new Intl.DateTimeFormat('en-US', { timeZone: input || fallback }); return input || fallback; }
    catch { return fallback; }
}
export function utcTimestamp(raw: unknown): Date | null {
    const value = String(raw ?? '');
    if (!value) return null;
    const date = new Date(value.replace(' ', 'T') + (/Z$|[+-]\d\d:\d\d$/.test(value) ? '' : 'Z'));
    return Number.isNaN(date.getTime()) ? null : date;
}
export function sessionLabels(raw: unknown, timezone?: string): { date: string; time: string; listing: string } {
    const date = utcTimestamp(raw);
    if (!date) return { date: raw ? String(raw) : 'None', time: '', listing: raw ? String(raw) : 'None' };
    const timeZone = viewerTimezone(timezone);
    const part = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short' }).formatToParts(date).map(p => [p.type, p.value]));
    let abbreviation = part.timeZoneName;
    for (const locale of ['en-US', 'en-GB', 'en-AU', 'en-IN', 'ja-JP']) {
        const label = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' }).formatToParts(date).find(p => p.type === 'timeZoneName')?.value;
        if (label && !/^(GMT|UTC)[+\-−]/.test(label)) { abbreviation = label; break; }
    }
    return { date: new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric', year: 'numeric' }).format(date),
        time: `${Number(part.hour)}:${part.minute} ${part.dayPeriod} ${abbreviation}`,
        listing: `${part.year}-${part.month}-${part.day} ${part.hour}:${part.minute} ${part.dayPeriod}` };
}

function headerPicture(row: CampaignListRow, data: CampaignListResponse): string {
    const pictures = rawListArray(row.pictures).map(rawListRecord).filter(p => data.mode === 'dashboard' || Number(p.picture_private ?? 0) === 0
        || data.viewer_staff || (String(p.picture_created_by_id ?? '') !== '' && String(p.picture_created_by_id).toUpperCase() === String(data.viewer_id ?? '').toUpperCase()));
    pictures.sort((a, b) => Number(b.primary_picture) - Number(a.primary_picture) || Number(b.picture_thumbnail) - Number(a.picture_thumbnail)
        || Number(a.picture_number) - Number(b.picture_number) || Number(a.picture_i) - Number(b.picture_i));
    const first = pictures[0];
    return first ? String(rawListRecord(compilePictureRow(first, { cdnUrl: 'https://cdn.fatetide.com/' })).picture_url)
        : `https://cdn.fatetide.com/images/defaults/campaign_headers/default_campaign_header_${1 + Math.floor(Math.random() * 7)}.jpg`;
}
function common(row: CampaignListRow, data: CampaignListResponse, root: string) {
    const name = plain(row.campaign_name);
    const type = String(row.campaign_type ?? '');
    return { item_id: row.campaign_id, name, url: new URL(`campaign.php?campaign_id=${encodeURIComponent(row.campaign_id)}`, root).href,
        picture_url: headerPicture(row, data), level: String(campaignLevel(row)),
        type_label: type.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase()),
        type_badge_class: type === 'campaign' ? 'bg-secondary' : type === 'series' ? 'bg-success' : 'bg-warning text-dark',
        mic_required: Number(row.campaign_microphone_required) === 1, webcam_required: Number(row.campaign_webcam_required) === 1, text: name.toLowerCase() };
}
export function campaignGameCards(data: CampaignListResponse, root: string): GameCard[] {
    return data.campaigns.map(row => {
        const price = Number(row.campaign_session_price) || 0;
        return { ...common(row, data, root), level_num: campaignLevel(row), next_session_num: String(Number(row.count_past_sessions ?? 0) + 1),
            next_session_date: sessionLabels(row.listing_next_start, data.viewer_timezone).listing,
            seats_filled: String(row.count_campaign_players ?? 0), seats_max: String(row.campaign_max_players ?? 0), price,
            price_f: price > 0 ? '$' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '' };
    });
}
export type DashboardCampaignCard = MyCampaignCard & { csrf_token: string };
export function campaignDashboardCards(data: CampaignListResponse, root: string): DashboardCampaignCard[] {
    return data.campaigns.map(row => {
        const id = encodeURIComponent(row.campaign_id);
        const url = (route: string) => new URL(`${route}?campaign_id=${id}`, root).href;
        const code = String(row.campaign_invite_code ?? '');
        const labels = sessionLabels(row.next_session_start, viewerTimezone(data.viewer_timezone, 'America/Denver'));
        const requested = !!Number(row.requested_signup);
        return { ...common(row, data, root), url: url('my_campaign.php'), csrf_token: data.csrf_token ?? '',
            locations_url: url('tools/locations/my_locations.php'), encounters_url: url('tools/encounters/my_encounters.php'),
            players_url: url('tools/campaigns/my_campaign_players.php'), requested_signup: requested,
            join_requests_url: url('tools/campaigns/campaign_join_requests.php'), pending_join_requests: Number(row.pending_join_requests ?? 0),
            invite_code: code, invite_link: requested ? url('request_join_campaign.php') + `&campaign_invite_code=${encodeURIComponent(code)}`
                : new URL(`link_player.php?campaign_invite_code=${encodeURIComponent(code)}`, root).href,
            public_url: url('campaign.php'), edit_url: url('tools/campaigns/modify_campaign.php'),
            media_url: new URL(`tools/media/media_manager.php?type=campaign&id=${id}`, root).href,
            settings_url: url('tools/campaigns/my_campaign_settings.php'), share_settings_url: url('campaign_settings.php'),
            is_running: !row.campaign_end_date, is_private: Number(row.campaign_private) === 1,
            sessions_url: url('tools/campaign_sessions/modify_campaign_sessions.php'),
            next_session_number: row.next_session_start ? String(Number(row.count_past_sessions ?? 0) + 1) : 'None',
            next_session_date: labels.date, next_session_time: labels.time,
            seats_filled: Number(row.count_campaign_players ?? 0), max_players: Number(row.campaign_max_players ?? 0),
            world_id: String(row.world_id ?? ''), world_name: plain(row.world_name).replace(/_/g, ' ').replace(/\s+/g, ' ').trim(),
            calendar_id: String(row.calendar_id ?? ''), calendar_name: plain(row.calendar_name).replace(/_/g, ' ').replace(/\s+/g, ' ').trim(),
            current_date: '', current_time: '', start_date: String(row.campaign_start_date ?? ''), end_date: String(row.campaign_end_date ?? ''),
            client_campaign: { campaigns: row, computed: { campaign: { campaign_level: campaignLevel(row), calendar: campaignCalendar(row) } } } };
    });
}
